import dayjs from "dayjs";
import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { useSelector } from "react-redux";

import KPTextform from "../../KPTComponents/KPTextform";
import { QCV2026_SCORE_DEFAULTS } from "../../../constants/qcv2026.config";

// Từ khi form đăng ký lưu THẲNG code (qcv_score_infos) thay vì key cũ
// (q1/domestic/s1...), tra code không cần dict dịch nữa — chỉ cần biết
// TRƯỜNG nào trên info giữ giá trị phân loại, và mục (1-7) tương ứng.
// Bản ghi CŨ chưa migrate (vẫn còn key cũ, vd "q1") sẽ không khớp code nào
// trong scoreInformationList -> trả về undefined, giữ nguyên hành vi cũ
// ("Thành tích không thuộc hạng mục nào, hội đồng thư ký tự cho điểm").
const CODE_FIELD_BY_TYPE = {
  formSolution: { field: "type", muc: "1" },
  formScientificResearch: { field: "type", muc: "2" },
  formScientificReport: { field: "type", muc: "3" },
  formResearchProject: { field: "level", muc: "4" },
  formMonograph: { field: "type", muc: "5" },
  formTechCompany: { field: "applicationRange", muc: "6" },
  formReward: { field: "type", muc: "7" },
};

function DuyetThanhTichModal(props) {
  const [scoreInformation, setScoreInformation] = useState(null);

  const scoreInformationList = useSelector((state) =>
    state.scoreInformationReducer.scoreInformationList?.filter(
      (e) => e.type === "qcv"
    )
  );

  // Lấy thông tin điểm theo code: ưu tiên cấu hình QcvScoreInfo (admin),
  // nếu chưa có thì dùng bảng điểm mặc định QCV 2026.
  const getScoreInfo = (c) => {
    const configured = scoreInformationList?.find((e) => e?.code == c);
    if (configured && configured.value != null) return configured;
    const def = QCV2026_SCORE_DEFAULTS[c];
    return def ? { code: c, ...def } : configured;
  };

  const code = useMemo(() => {
    const cfg = CODE_FIELD_BY_TYPE[props.type];
    if (!cfg) return undefined;
    const value = props.info?.[cfg.field];
    if (!value) return undefined;
    const isValidCode = scoreInformationList?.some(
      (e) => e.code === value && String(e.code).startsWith(`${cfg.muc}.`)
    );
    return isValidCode ? value : undefined;
  }, [props.info, props.type, scoreInformationList]);

  useEffect(() => {
    if (code) {
      var info;
      var bonus = null;
      const minBonusYear = dayjs().year() - 3 + 1;
      const sInfo = getScoreInfo(code);

      if (
        props.type === "formScientificResearch" ||
        props.type === "formScientificReport" ||
        props.type === "formMonograph"
      ) {
        const diemChiaDeu = sInfo?.value / 2 / props.info?.numberAuthor;
        const diemTgChinh =
          props.info?.role === "main"
            ? sInfo?.value / 2 / props.info?.numberMain
            : 0;
        const tong = diemChiaDeu + diemTgChinh;
        if (props.info?.publishYear >= minBonusYear) {
          bonus = {
            goc: tong,
            text: (
              <>
                Năm xuất bản của thành tích là <b>{props.info?.publishYear}</b>,
                ứng viên được cộng thêm <b>10%</b> điểm của nội dung đánh giá.
              </>
            ),
            diem: tong * 0.1,
          };
        }
        info = {
          ...sInfo,
          bonus,
          value: Math.round((tong + (bonus?.diem || 0)) * 100) / 100,
        };
      } else if (props.type === "formSolution") {
        // Sáng chế/giải pháp: tính như bài báo — chia số tác giả + số tác giả chính.
        const numberAuthor = props.info?.numberAuthor || 1;
        const numberMain = props.info?.numberMain || 1;
        const tong =
          sInfo?.value / 2 / numberAuthor + sInfo?.value / 2 / numberMain;
        if (
          parseInt(props.info?.issueAt.split("/")?.slice(-1)?.[0]) >=
          minBonusYear
        ) {
          bonus = {
            goc: tong,
            text: (
              <>
                Ngày cấp của thành tích là <b>{props.info?.issueAt}</b>, ứng
                viên được cộng thêm <b>10%</b> điểm của nội dung đánh giá.
              </>
            ),
            diem: tong * 0.1,
          };
        }
        info = {
          ...sInfo,
          bonus,
          numberAuthor,
          numberMain,
          value: Math.round((tong + (bonus?.diem || 0)) * 100) / 100,
        };
      } else if (props.type === "formResearchProject") {
        // Chủ trì (d1): hưởng trọn; Đồng chủ trì (d2): chia đều theo số đồng chủ trì.
        const divisor =
          props.info?.type === "d2" ? props.info?.numberMain || 1 : 1;
        const tong = sInfo?.value / divisor;
        if (dayjs(props.info?.start_at).year() >= minBonusYear) {
          bonus = {
            goc: tong,
            text: (
              <>
                Thời gian kết thúc của thành tích là{" "}
                <b>{dayjs(props.info?.start_at).format("DD/MM/YYYY")}</b>, ứng
                viên được cộng thêm <b>10%</b> điểm của nội dung đánh giá.
              </>
            ),
            diem: tong * 0.1,
          };
        }
        info = {
          ...sInfo,
          bonus,
          value: Math.round((tong + (bonus?.diem || 0)) * 100) / 100,
        };
      } else if (props.type === "formTechCompany") {
        const tong = sInfo?.value;
        if (props.info?.publicYear >= minBonusYear) {
          bonus = {
            goc: tong,
            text: (
              <>
                Năm xuất bản của thành tích là <b>{props.info?.publicYear}</b>,
                ứng viên được cộng thêm <b>10%</b> điểm của nội dung đánh giá.
              </>
            ),
            diem: tong * 0.1,
          };
        }
        info = {
          ...sInfo,
          bonus,
          value: Math.round((tong + (bonus?.diem || 0)) * 100) / 100,
        };
      } else if (props.type === "formReward") {
        const tong = sInfo?.value;
        if (
          parseInt(props.info?.decisionDay.split("/")?.slice(-1)?.[0]) >=
          minBonusYear
        ) {
          bonus = {
            goc: tong,
            text: (
              <>
                Ngày quyết định khen thưởng là <b>{props.info?.decisionDay}</b>,
                ứng viên được cộng thêm <b>10%</b> điểm của nội dung đánh giá.
              </>
            ),
            diem: tong * 0.1,
          };
        }
        info = {
          ...sInfo,
          bonus,
          value: Math.round((tong + (bonus?.diem || 0)) * 100) / 100,
        };
      }

      setScoreInformation(info);
    }
  }, [code]);

  useEffect(() => {
    props.setValues({
      ...props.values,
      score: scoreInformation?.value || 0,
    });
  }, [scoreInformation]);

  const textForm = [
    {
      key: "score",
      width: 12,
      label: "Điểm",
      value: props.values?.score,
      type: "float",
      required: true,
    },
    {
      key: "note",
      width: 12,
      height: 3,
      label: "Ghi chú của thư ký (Trong trường hợp sửa điểm)",
      value: props.values?.note,
    },
    { key: "submit", width: 12, label: "Duyệt thành tích", type: "submit" },
  ];

  return (
    <Dialog fullWidth maxWidth="xs" open={props.open}>
      <DialogTitle style={{ padding: "4px 12px 4px 24px" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">Duyệt thành tích</Typography>
          <IconButton
            onClick={() => {
              props.handleClose();
              props.setValues(null);
              setScoreInformation(null);
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent style={{ paddingBottom: 16 }}>
        <Typography style={{ marginBottom: 32, fontSize: 17 }}>
          {!scoreInformation?.description || scoreInformationList?.value ? (
            props.type === "formHrDevelopment" ? (
              "Hội đồng thư ký xét duyệt xem thông tin đã chính xác chưa, hệ thống sẽ tự tính điểm thành tích."
            ) : (
              "Thành tích không thuộc hạng mục nào, hội đồng thư ký tự cho điểm."
            )
          ) : (
            <>
              {"Thành tích thuộc hạng mục "}
              <b>{scoreInformation?.description}</b>
              {", với số điểm tối đa là: "}
              <b>
                {getScoreInfo(code)?.value} điểm.
              </b>
              <br />
              <br />

              {(props.type === "formScientificResearch" ||
                props.type === "formScientificReport" ||
                props.type === "formMonograph") && (
                <>
                  {"Thành tích có "}
                  <b>{props.info?.numberAuthor}</b>
                  {" tác giả tham gia (Với "}
                  <b>{props.info?.numberMain}</b>
                  {" tác giả chính (hoặc tác giả liên hệ)). Trong đó, ứng viên"}
                  {" là "}
                  <b>
                    {props.info?.role === "main"
                      ? "Tác giả chính (hoặc tác giả liên hệ)"
                      : "Đồng tác giả"}
                  </b>
                  {" nên được hưởng "}
                  <b>
                    {props.info?.role === "main"
                      ? `1/2 x 1/${props.info?.numberMain} + 1/2 x 1/${
                          props.info?.numberAuthor
                        } = ${
                          Math.round(
                            (1 / 2 / props.info?.numberMain +
                              1 / 2 / props.info?.numberAuthor) *
                              100
                          ) / 100
                        }`
                      : `1/2 x 1/${props.info?.numberAuthor} = 1/${
                          2 * props.info?.numberAuthor
                        }`}
                  </b>
                  {" tổng số điểm."}
                  <br />
                  <br />
                </>
              )}

              {props.type === "formSolution" && (
                <>
                  {"Thành tích có "}
                  <b>{scoreInformation?.numberAuthor}</b>
                  {" tác giả tham gia (Với "}
                  <b>{scoreInformation?.numberMain}</b>
                  {" tác giả chính (hoặc tác giả liên hệ)) nên ứng viên được hưởng "}
                  <b>
                    {`1/2 x 1/${scoreInformation?.numberMain} + 1/2 x 1/${
                      scoreInformation?.numberAuthor
                    } = ${
                      Math.round(
                        (1 / 2 / (scoreInformation?.numberMain || 1) +
                          1 / 2 / (scoreInformation?.numberAuthor || 1)) *
                          100
                      ) / 100
                    }`}
                  </b>
                  {" tổng số điểm."}
                  <br />
                  <br />
                </>
              )}

              {props.type === "formResearchProject" &&
                props.info?.type === "d1" &&
                props.info?.numberMain > 1 && (
                  <>
                    {"Chương trình, dự án, đề tài có "}
                    <b>{props.info?.numberMain}</b>
                    {" chủ trì nên ứng viên được hưởng "}
                    <b>{`1/${props.info?.numberMain} `}</b>
                    {"tổng số điểm."}
                    <br />
                    <br />
                  </>
                )}

              {scoreInformation?.bonus && (
                <>
                  Ứng viên được: <b>{scoreInformation?.bonus?.goc} điểm.</b>
                  <br />
                  <br />
                  {scoreInformation?.bonus?.text}
                  <br />
                  <br />
                </>
              )}

              {"Vậy ứng viên được tối đa: "}
              <b>{scoreInformation?.value} điểm.</b>
            </>
          )}
        </Typography>
        <KPTextform
          textForm={
            !(
              (!scoreInformation?.description || scoreInformationList?.value) &&
              props.type === "formHrDevelopment"
            )
              ? textForm
              : [
                  {
                    key: "note",
                    width: 12,
                    height: 3,
                    label: "Ghi chú của thư ký (Trong trường hợp sửa điểm)",
                    value: props.values?.note,
                  },
                  {
                    key: "save-note",
                    width: 6,
                    label: "Lưu G/C (Không duyệt)",
                    type: "button",
                    color: "default",
                    variant: "outlined",
                    onClick: () => props.saveNote(),
                  },
                  {
                    key: "submit",
                    width: 6,
                    label: "Duyệt thành tích",
                    type: "submit",
                  },
                ]
          }
          values={props.values}
          setValues={props.setValues}
          handleSubmit={props.handleSubmit}
        />
      </DialogContent>
    </Dialog>
  );
}

export default DuyetThanhTichModal;
